import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorMode,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { ReactComponent as CheckmarkIcon } from 'resources/images/checkmark.svg';
import { useIsMobile } from 'app/utils/hooks';
import { ThemeMode } from 'types/enums';
import { useAppDispatch, useAppSelector } from 'app/store';
import { setThemeModeAction } from 'app/store/Settings/slice';
import { getThemeModeSelector } from 'app/store/Settings/selectors';
import { upperFirst } from 'lodash';

const MIN_BUTTON_WIDTH = '88px';

export const ThemeSettings = () => {
  const isMobileView = useIsMobile(false);
  const styles = useMultiStyleConfig('SettingsPageList', {
    isMobile: isMobileView,
    minButtonWidth: MIN_BUTTON_WIDTH,
  });

  const { setColorMode } = useColorMode();
  const themeMode = useAppSelector(getThemeModeSelector);

  const primaryText = 'Appearance';
  const secondaryText = upperFirst(themeMode);

  const options = [
    {
      title: 'Light',
      value: ThemeMode.LIGHT,
    },
    {
      title: 'Dark',
      value: ThemeMode.DARK,
    },
    {
      title: 'System',
      value: ThemeMode.SYSTEM,
    },
  ];

  const dispatch = useAppDispatch();
  const onChange = (value: ThemeMode) => {
    dispatch(setThemeModeAction(value));
    setColorMode(value);
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.textWrapper}>
        <Text sx={styles.primaryText} pt={2} title={primaryText}>
          {primaryText}
        </Text>
        <Text
          sx={styles.secondaryText}
          data-testid="secondary-privacy-text"
          title={secondaryText}
        >
          {secondaryText}
        </Text>
      </Box>
      <Menu>
        <Button
          variant="secondary"
          size="sm"
          as={MenuButton}
          data-testid="change-theme-button"
          minW={MIN_BUTTON_WIDTH}
        >
          Change
        </Button>
        <MenuList>
          {options.map((option) => (
            <MenuItem
              h="32px"
              onClick={() => {
                onChange(option.value);
              }}
              key={option.value}
              value={option.value}
              data-testid="theme-option"
            >
              <Flex flex={1} justifyContent="space-between">
                <Text fontSize="xs">{option.title}</Text>
                {themeMode === option.value && (
                  <Icon as={CheckmarkIcon} color="iconAccent" w={3} h={3} />
                )}
              </Flex>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};
